import { RefObject, useEffect, useRef } from "react";

export type AutoFocusFirstFieldOptions = {
  enabled?: boolean;
  /**
   * Focus once per enabled cycle (resets when `enabled` becomes false).
   */
  once?: boolean;
  /**
   * Wait until the container is in viewport using IntersectionObserver.
   * If false, the hook runs immediately after mount.
   */
  whenVisible?: boolean;
  delayMs?: number;
  /**
   * Retries when the container or target is not ready yet (common in modals).
   */
  retryAttempts?: number;
  retryIntervalMs?: number;
  /**
   * Preset for Ant Design modals/drawers: deferred focus with retries.
   */
  modal?: boolean;
  /**
   * Preset for full-page forms: wait until visible, short initial delay.
   */
  page?: boolean;
  /**
   * Optional override: CSS selectors to find candidate focus targets
   * inside the container.
   */
  selectors?: string[];
};

const DEFAULT_SELECTORS: string[] = [
  "[data-autofocus='true']",
  "input:not([type='hidden'])",
  "textarea",
  "select",
  "[contenteditable='true']",
  "[role='combobox']"
];

const MODAL_FOCUS_DEFAULTS = {
  whenVisible: false,
  delayMs: 200,
  retryAttempts: 5,
  retryIntervalMs: 100
} as const;

const PAGE_FOCUS_DEFAULTS = {
  whenVisible: true,
  delayMs: 50,
  retryAttempts: 0
} as const;

const DEFAULT_OPTIONS = {
  enabled: true,
  once: true,
  modal: false,
  whenVisible: true,
  delayMs: 0,
  retryAttempts: 0,
  retryIntervalMs: 100,
  selectors: DEFAULT_SELECTORS
};

function resolveOptions(options?: AutoFocusFirstFieldOptions) {
  if (options?.modal) {
    return { ...DEFAULT_OPTIONS, ...MODAL_FOCUS_DEFAULTS, ...options };
  }
  if (options?.page) {
    return { ...DEFAULT_OPTIONS, ...PAGE_FOCUS_DEFAULTS, ...options };
  }
  return { ...DEFAULT_OPTIONS, ...options };
}

function isElementFocusable(el: HTMLElement) {
  try {
    const style = window.getComputedStyle(el);

    if (style.display === "none" || style.visibility === "hidden") return false;
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-disabled") === "true") return false;

    if ((el as HTMLInputElement).readOnly) return false;
    if (el.getAttribute("tabindex") === "-1") return false;

    return true;
  } catch {
    // Defensive: window.getComputedStyle can throw on a detached/exotic node.
    /* v8 ignore next */
    return false;
  }
}

const TEXT_INPUT_TYPES = new Set([
  "text",
  "password",
  "email",
  "search",
  "url",
  "tel",
  "number"
]);

function isTextEntryElement(el: HTMLElement) {
  const tagName = el.tagName.toLowerCase();
  if (tagName === "textarea") return true;
  if (tagName !== "input") return el.isContentEditable;

  // A real <input> element always reports a non-empty `.type` (defaults to
  // "text"), so the `|| "text"` fallback branch is unreachable in the DOM.
  /* v8 ignore next */
  const type = ((el as HTMLInputElement).type || "text").toLowerCase();
  return TEXT_INPUT_TYPES.has(type);
}

function findFirstFocusable(
  container: HTMLElement,
  selectors: string[]
): HTMLElement | null {
  try {
    const query = selectors.join(",");
    const nodes = Array.from(container.querySelectorAll<HTMLElement>(query));
    for (const node of nodes) {
      if (isElementFocusable(node)) {
        return node;
      }
    }
  } catch {
    // Invalid selectors or detached container
  }
  return null;
}

export const useAutoFocusFirstField = (
  containerRef: RefObject<HTMLElement | null | undefined>,
  options?: AutoFocusFirstFieldOptions
) => {
  const {
    enabled,
    once,
    whenVisible,
    delayMs,
    retryAttempts,
    retryIntervalMs,
    selectors
  } = resolveOptions(options);

  const didFocusRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      didFocusRef.current = false;
      return;
    }
    if (once && didFocusRef.current) return;

    let cancelled = false;
    const timeouts: number[] = [];
    const schedule = (ms: number, fn: () => void) => {
      timeouts.push(
        window.setTimeout(() => {
          // Pending timeouts are cleared on teardown, so a callback never fires
          // after cancellation — the `cancelled` short-circuit is defensive.
          /* v8 ignore next */
          if (!cancelled) fn();
        }, ms)
      );
    };

    const focusIfNeeded = (): boolean => {
      // Only invoked from attempt(), which already bails when cancelled, so this
      // guard never trips — kept as defensive depth.
      /* v8 ignore next */
      if (cancelled) return false;

      const container = containerRef.current;
      if (!container) return false;

      const activeEl = document.activeElement as HTMLElement | null;
      if (activeEl && activeEl !== document.body) {
        if (container.contains(activeEl)) {
          didFocusRef.current = true;
          return true;
        }
        if (isTextEntryElement(activeEl)) return false;
      }

      const target = findFirstFocusable(container, selectors);
      if (!target) return false;

      try {
        target.focus({ preventScroll: true } as FocusOptions);
        didFocusRef.current = true;
        return true;
      } catch {
        return false;
      }
    };

    const attempt = (index: number) => {
      // schedule() already guards on !cancelled and no further attempt is queued
      // once focus succeeds, so this guard is defensive depth.
      /* v8 ignore next */
      if (cancelled || (once && didFocusRef.current)) return;
      if (focusIfNeeded()) return;
      if (index < retryAttempts) {
        schedule(retryIntervalMs, () => attempt(index + 1));
      }
    };

    const startAttempts = () => {
      // Every caller invokes this only while not cancelled (initial effect run or
      // the IntersectionObserver callback, which checks cancelled first).
      /* v8 ignore next */
      if (cancelled) return;
      schedule(delayMs, () => attempt(0));
    };

    const teardown = () => {
      cancelled = true;
      timeouts.forEach((id) => window.clearTimeout(id));
    };

    if (!whenVisible || typeof window === "undefined") {
      startAttempts();
      return teardown;
    }

    if (!("IntersectionObserver" in window)) {
      startAttempts();
      return teardown;
    }

    const container = containerRef.current;
    if (!container) {
      startAttempts();
      return teardown;
    }

    const io = new IntersectionObserver(
      (entries) => {
        // The observer is disconnected on teardown, so this callback won't fire
        // after cancellation — guard is defensive against a late queued callback.
        /* v8 ignore next */
        if (cancelled) return;
        if (entries.some((e) => e.isIntersecting)) {
          startAttempts();
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    io.observe(container);
    return () => {
      cancelled = true;
      io.disconnect();
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [
    containerRef,
    delayMs,
    enabled,
    once,
    retryAttempts,
    retryIntervalMs,
    selectors,
    whenVisible
  ]);
};
